import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminSidebar from '../../components/Layout/SuperAdminSidebar';


// Import Material UI components
import {
  Box, Typography, Paper, TextField, Button, FormControlLabel, 
  Switch, Divider, Alert, CircularProgress, Grid, Card, 
  CardContent, CardHeader, Tabs, Tab, InputAdornment,
  Snackbar, IconButton
} from '@mui/material';

// Import Material UI icons
import {
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Sync as SyncIcon
} from '@mui/icons-material';

const SuperAdminPaymentSettings = () => {
  const navigate = useNavigate();
  
  // State for form values and UI
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showApiKeys, setShowApiKeys] = useState(false);
  
  // Platform Stripe configuration
  const [config, setConfig] = useState({
    stripePublishableKey: '',
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    testMode: true,
    platformFeePercentage: 2.5,
    platformFeeFixed: 0.30,
    autoPayoutEnabled: false,
    payoutSchedule: 'weekly',
    notifyAdminOnPayment: true,
    notifyTenantOnPayment: true
  });

  useEffect(() => {
    const loadPlatformConfig = async () => {
      try {
        setLoading(true);
        
        // Try to load from the API through superAdminStripeService
        try {
          const response = await superAdminStripeService.getPlatformConfig();
          if (response && response.data) {
            setConfig({
              ...config,
              ...response.data,
              stripeSecretKey: response.data.stripeSecretKey || '',
              testMode: response.data.testMode !== false
            });
            setLoading(false);
            return;
          }
        } catch (apiError) {
          console.error("Failed to load from API:", apiError);
          // Fall back to mock data if API fails
        }
        
        // For demo purposes, use mock data
        setTimeout(() => {
          setConfig({
            stripePublishableKey: 'pk_test_sample123456789',
            stripeSecretKey: 'sk_test_sample123456789',
            stripeWebhookSecret: 'whsec_sample123456789',
            testMode: true,
            platformFeePercentage: 2.5,
            platformFeeFixed: 0.30,
            autoPayoutEnabled: false,
            payoutSchedule: 'weekly',
            notifyAdminOnPayment: true,
            notifyTenantOnPayment: true
          });
          setLoading(false);
        }, 1000);
      } catch (err) {
        console.error("Failed to load platform configuration:", err);
        setError("Failed to load platform configuration. Please refresh and try again.");
        setLoading(false);
      }
    };
    
    loadPlatformConfig();
  }, []);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    if (type === 'checkbox') {
      setConfig(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'number') {
      setConfig(prev => ({
        ...prev,
        [name]: parseFloat(value)
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Try to save through the API
      try {
        await superAdminStripeService.updatePlatformConfig({
          stripePublishableKey: config.stripePublishableKey,
          stripeSecretKey: config.stripeSecretKey,
          stripeWebhookSecret: config.stripeWebhookSecret,
          testMode: config.testMode,
          platformFeePercentage: config.platformFeePercentage,
          platformFeeFixed: config.platformFeeFixed,
          autoPayoutEnabled: config.autoPayoutEnabled,
          payoutSchedule: config.payoutSchedule,
          notifyAdminOnPayment: config.notifyAdminOnPayment,
          notifyTenantOnPayment: config.notifyTenantOnPayment
        });
        
        setSuccess('Platform payment configuration saved successfully.');
        setTimeout(() => setSuccess(null), 3000);
        setSaving(false);
        return;
      } catch (apiError) {
        console.error("API save failed:", apiError);
        // Fall back to simulation if API fails
      }
      
      // Simulate API call with timeout for demo purposes
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSuccess('Platform payment configuration saved successfully.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Failed to save platform configuration:", err);
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  
  const toggleApiKeysVisibility = () => {
    setShowApiKeys(!showApiKeys);
  };
  
  const handleSyncWithStripe = () => {
    // This would synchronize with Stripe to verify account status,
    // refresh capabilities, update webhook endpoints, etc.
    setSuccess('Successfully synchronized with Stripe.');
    setTimeout(() => setSuccess(null), 3000);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 3,
        bgcolor: '#f5f7fa',
      }}
    >
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
          <PaymentIcon sx={{ mr: 1 }} /> Platform Payment Configuration
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          Configure system-wide Stripe payment settings
        </Typography>
      </Paper>

      {/* Form Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                backgroundColor: '#f5f5f5',
              }}
            >
              <Tab 
                label="API Keys & Credentials" 
                icon={<CreditCardIcon />} 
                iconPosition="start" 
              />
              <Tab 
                label="Fee & Payout Settings" 
                icon={<SettingsIcon />} 
                iconPosition="start" 
              />
            </Tabs>

            {/* Tab 1: API Keys & Credentials */}
            {activeTab === 0 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">
                    Stripe API Credentials
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<SyncIcon />}
                    onClick={handleSyncWithStripe}
                  >
                    Sync with Stripe
                  </Button>
                </Box>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={config.testMode}
                      onChange={handleChange}
                      name="testMode"
                      color="primary"
                    />
                  }
                  label="Use Stripe Test Mode"
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                  {config.testMode ? 
                    "The platform is in test mode. No real charges will be made." : 
                    "The platform is in LIVE mode. Real charges will be made to customers."}
                </Typography>
                
                <TextField
                  label="Stripe Publishable Key"
                  fullWidth
                  margin="normal"
                  name="stripePublishableKey"
                  value={config.stripePublishableKey}
                  onChange={handleChange}
                  type={showApiKeys ? "text" : "password"}
                  variant="outlined"
                  placeholder="pk_test_..."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle key visibility"
                          onClick={toggleApiKeysVisibility}
                          edge="end"
                        >
                          {showApiKeys ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  label="Stripe Secret Key"
                  fullWidth
                  margin="normal"
                  name="stripeSecretKey"
                  value={config.stripeSecretKey}
                  onChange={handleChange}
                  type={showApiKeys ? "text" : "password"}
                  variant="outlined"
                  placeholder="sk_test_..."
                />
                
                <TextField
                  label="Stripe Webhook Secret"
                  fullWidth
                  margin="normal"
                  name="stripeWebhookSecret"
                  value={config.stripeWebhookSecret}
                  onChange={handleChange}
                  type={showApiKeys ? "text" : "password"}
                  variant="outlined"
                  placeholder="whsec_..."
                />
                
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Webhook Endpoint URL
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Configure this URL in your Stripe Dashboard:
                  </Typography>
                  <Typography component="code" sx={{ 
                    display: 'block',
                    mt: 1,
                    p: 1,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1
                  }}>
                    {`${window.location.origin}/api/webhooks/platform`}
                  </Typography>
                </Alert>
              </Box>
            )}
            
            {/* Tab 2: Fee & Payout Settings */}
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Platform Fee Settings
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  These fees will be charged to tenants in addition to Stripe's standard processing fees
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Platform Fee Percentage"
                      type="number"
                      inputProps={{ step: "0.01", min: "0" }}
                      fullWidth
                      margin="normal"
                      name="platformFeePercentage"
                      value={config.platformFeePercentage}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Platform Fee Fixed Amount"
                      type="number"
                      inputProps={{ step: "0.01", min: "0" }}
                      fullWidth
                      margin="normal"
                      name="platformFeeFixed"
                      value={config.platformFeeFixed}
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 4 }} />
                
                <Typography variant="h6" gutterBottom>
                  Payout Settings
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={config.autoPayoutEnabled}
                      onChange={handleChange}
                      name="autoPayoutEnabled"
                      color="primary"
                    />
                  }
                  label="Enable Automatic Payouts to Connected Accounts"
                />
                
                <TextField
                  select
                  label="Payout Schedule"
                  fullWidth
                  margin="normal"
                  name="payoutSchedule"
                  value={config.payoutSchedule}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={!config.autoPayoutEnabled}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </TextField>
                
                <Divider sx={{ my: 4 }} />
                
                <Typography variant="h6" gutterBottom>
                  Notification Settings
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={config.notifyAdminOnPayment}
                      onChange={handleChange}
                      name="notifyAdminOnPayment"
                      color="primary"
                    />
                  }
                  label="Notify Platform Admin on New Payments"
                />
                
                <FormControlLabel
                  control={
                    <Switch 
                      checked={config.notifyTenantOnPayment}
                      onChange={handleChange}
                      name="notifyTenantOnPayment"
                      color="primary"
                    />
                  }
                  label="Notify Tenant Admin on New Payments"
                />
              </Box>
            )}
            
            {/* Action buttons */}
            <Box sx={{ p: 3, bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveConfig}
                disabled={saving}
                sx={{
                  background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                  boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Save Configuration'}
              </Button>
            </Box>
          </Paper>

          {/* Show notification messages */}
          <Snackbar 
            open={!!error || !!success} 
            autoHideDuration={6000} 
            onClose={() => { setError(null); setSuccess(null); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => { setError(null); setSuccess(null); }}
              severity={error ? "error" : "success"}
              variant="filled"
            >
              {error || success}
            </Alert>
          </Snackbar>
        </Grid>
        
        {/* Information sidebar */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader title="Stripe Account Status" />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="body1">
                  Account verified and active
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Connected account status: <strong>Active</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {config.testMode ? (
                  <strong>TEST MODE is enabled</strong>
                ) : (
                  <strong style={{ color: '#d32f2f' }}>LIVE MODE is enabled</strong>
                )}
              </Typography>
            </CardContent>
          </Card>
          
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardHeader title="Help & Documentation" />
            <CardContent>
              <Typography variant="body2" paragraph>
                Learn more about configuring Stripe for your platform:
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                fullWidth
                href="https://stripe.com/docs/connect/platform-capabilities-overview"
                target="_blank"
                sx={{ mb: 2 }}
              >
                Stripe Platform Documentation
              </Button>
              <Button 
                variant="outlined" 
                color="primary" 
                fullWidth
                href="https://dashboard.stripe.com"
                target="_blank"
              >
                Go to Stripe Dashboard
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SuperAdminPaymentSettings;
